import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight, Server, Gauge, Mail, Globe, ShieldAlert } from "lucide-react";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/Button";
import { services } from "@/data/site";
import { zohoNav } from "@/data/zohoSolutions";
import { absoluteNav } from "@/data/absolutePages";

// Hostinger offer menu — all options route to the promo landing page.
const hostingerNav = [
  { label: "Web Hosting", to: "/hostinger-offer", icon: Server },
  { label: "VPS Hosting", to: "/hostinger-offer", icon: Gauge },
  { label: "Business Email", to: "/hostinger-offer", icon: Mail },
  { label: "Domains", to: "/hostinger-offer", icon: Globe },
];

const linkCls = (dark) => ({ isActive }) =>
  `transition-colors ${isActive ? (dark ? "text-white" : "text-cobalt") : (dark ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-ink")}`;

const triggerCls = (active, dark) =>
  `flex items-center gap-1 text-sm font-medium transition-colors ${active ? (dark ? "text-white" : "text-cobalt") : (dark ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-ink")}`;

function ServicesMenu({ open, setOpen, dark }) {
  const id = "services";
  return (
    <div className="relative" onMouseEnter={() => setOpen(id)} onMouseLeave={() => setOpen(null)}>
      <button data-testid="nav-dd-services" className={triggerCls(open === id, dark)}>
        Services <ChevronDown size={15} className={`transition-transform ${open === id ? "rotate-180" : ""}`} />
      </button>
      {open === id && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
          <div className="w-[92vw] max-w-[720px] bg-white border border-slate-200 rounded-2xl shadow-[0_24px_60px_rgb(15,23,42,0.12)] p-3">
            <div className="grid grid-cols-2 gap-1">
              {services.map((s) => {
                const Icon = s.icon;
                return (
                  <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-item-service-${s.slug}`}
                    className="flex items-start gap-3 p-3 rounded-xl hover:bg-mist-dark transition-colors group">
                    <span className="w-9 h-9 rounded-lg bg-cobalt-light flex items-center justify-center shrink-0 group-hover:bg-cobalt transition-colors">
                      <Icon size={17} className="text-cobalt group-hover:text-white transition-colors" />
                    </span>
                    <span>
                      <span className="block font-semibold text-ink text-sm leading-tight">{s.title}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">{s.short}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
            <Link to="/services" data-testid="nav-item-all-services" className="mt-2 flex items-center justify-center gap-1 text-sm font-semibold text-cobalt border-t border-slate-100 pt-3 hover:gap-2 transition-all">
              View all services <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductsMenu({ open, setOpen, dark }) {
  const id = "products";
  const columns = [
    { logo: "/zoho-logo.png", alt: "Zoho", tag: "Business Suite", items: zohoNav, imgClass: "h-6" },
    { logo: "/absolute-logo.webp", alt: "Absolute", tag: "Zero Trust Security", items: absoluteNav, imgClass: "h-6" },
    { logo: "/hostinger-logo.webp", alt: "Hostinger", tag: "20% Off Hosting", items: hostingerNav, imgClass: "h-6" },
  ];
  return (
    <div className="relative" onMouseEnter={() => setOpen(id)} onMouseLeave={() => setOpen(null)}>
      <button data-testid="nav-dd-products" className={triggerCls(open === id, dark)}>
        Products <ChevronDown size={15} className={`transition-transform ${open === id ? "rotate-180" : ""}`} />
      </button>
      {open === id && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
          <div className="w-[92vw] max-w-[860px] bg-white border border-slate-200 rounded-2xl shadow-[0_24px_60px_rgb(15,23,42,0.12)] p-4">
            <div className="grid grid-cols-3 gap-x-6 gap-y-1">
              {columns.map((col) => (
                <div key={col.alt}>
                  <div className="flex items-center gap-2.5 px-2 pb-3 mb-1 border-b border-slate-100">
                    <img src={col.logo} alt={col.alt} className={`${col.imgClass} w-auto object-contain`} />
                    <span className="label text-[10px] text-slate-400">{col.tag}</span>
                  </div>
                  {col.items.map((c) => {
                    const CIcon = c.icon;
                    return (
                      <Link key={c.label} to={c.to} data-testid={`nav-item-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-mist-dark transition-colors group">
                        <span className="w-8 h-8 rounded-lg bg-cobalt-light flex items-center justify-center shrink-0 group-hover:bg-cobalt transition-colors">
                          <CIcon size={15} className="text-cobalt group-hover:text-white transition-colors" />
                        </span>
                        <span className="text-sm font-medium text-ink">{c.label}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
            <Link to="/products" data-testid="nav-item-all-products" className="mt-3 flex items-center justify-center gap-1 text-sm font-semibold text-cobalt border-t border-slate-100 pt-3 hover:gap-2 transition-all">
              All Products &amp; Partners <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function NoticesMenu({ open, setOpen, dark }) {
  const id = "notices";
  return (
    <div className="relative" onMouseEnter={() => setOpen(id)} onMouseLeave={() => setOpen(null)}>
      <button data-testid="nav-dd-notices" className={triggerCls(open === id, dark)}>
        Notices <ChevronDown size={15} className={`transition-transform ${open === id ? "rotate-180" : ""}`} />
      </button>
      {open === id && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
          <div className="w-[340px] bg-white border border-slate-200 rounded-2xl shadow-[0_24px_60px_rgb(15,23,42,0.12)] p-3">
            <Link to="/PHP2Shell-security-advisory" data-testid="nav-item-notice-php2shell"
              className="flex items-start gap-3 p-3 rounded-xl hover:bg-mist-dark transition-colors group">
              <span className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0 group-hover:bg-red-600 transition-colors">
                <ShieldAlert size={17} className="text-red-600 group-hover:text-white transition-colors" />
              </span>
              <span>
                <span className="block font-semibold text-ink text-sm leading-tight">PHP2Shell Security Advisory</span>
                <span className="block text-xs text-slate-500 mt-0.5">Critical PHP zero-day — patching &amp; remediation guidance (TS-SEC-2026-1096)</span>
              </span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dd, setDd] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setDd(null); }, [pathname]);

  const isDarkHero = pathname.startsWith("/products/absolute");
  const dark = isDarkHero && !scrolled;

  return (
    <header data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200" : "bg-transparent"}`}>
      {!["/php2shell-security-advisory", "/security-advisory"].includes(pathname.toLowerCase()) && (
        <Link to="/PHP2Shell-security-advisory" data-testid="security-advisory-bar" className="block bg-[#7F1D1D] text-white hover:bg-[#991B1B] transition-colors">
          <Marquee speed={45} gradient={false} pauseOnHover className="py-1.5 text-[11px] sm:text-xs font-semibold">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="mx-10 inline-flex items-center gap-2">
                <ShieldAlert size={13} className="text-red-300 shrink-0" />
                URGENT SECURITY ADVISORY (TS-SEC-2026-1096): Critical PHP zero-day "PHP2Shell" under active exploitation — unauthenticated remote code execution.
                <span className="underline underline-offset-2 whitespace-nowrap">Read the advisory &amp; remediation steps →</span>
              </span>
            ))}
          </Marquee>
        </Link>
      )}
      {pathname !== "/hostinger-offer" && (
        <Link to="/hostinger-offer" data-testid="announcement-bar"
          className="block text-center text-xs sm:text-sm font-semibold text-white py-2.5 px-4 transition-all hover:brightness-110"
          style={{ background: "linear-gradient(90deg,#673DE6,#8a5cf6)" }}>
          ⚡ Partner Offer: Get an exclusive <span className="underline underline-offset-2">20% off Hostinger Hosting</span> — free domain + free migration
        </Link>
      )}
      <div className="max-w-8xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={dark ? "/thinksmiths-logo-white.png" : "/thinksmiths-logo-black.png"} alt="The ThinkSmiths" className="h-16 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium">
          <NavLink to="/" data-testid="nav-link-home" className={linkCls(dark)}>Home</NavLink>
          <NavLink to="/about" data-testid="nav-link-about" className={linkCls(dark)}>About Us</NavLink>
          <ServicesMenu open={dd} setOpen={setDd} dark={dark} />
          <ProductsMenu open={dd} setOpen={setDd} dark={dark} />
          <NavLink to="/solutions" data-testid="nav-link-solutions" className={linkCls(dark)}>Solutions</NavLink>
          <NavLink to="/events" data-testid="nav-link-events" className={linkCls(dark)}>Events</NavLink>
          <NavLink to="/resources" data-testid="nav-link-resources" className={linkCls(dark)}>Resources</NavLink>
          <NoticesMenu open={dd} setOpen={setDd} dark={dark} />
        </nav>

        <div className="hidden lg:block">
          <Button to="/contact" data-testid="nav-contact-cta">Contact Us</Button>
        </div>

        <button data-testid="nav-mobile-toggle" className={`lg:hidden ${dark ? "text-white" : "text-ink"}`} onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-6 py-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <NavLink to="/" className="block text-slate-700 font-medium" data-testid="nav-mobile-home">Home</NavLink>
          <NavLink to="/about" className="block text-slate-700 font-medium" data-testid="nav-mobile-about">About Us</NavLink>
          <div className="border-t border-slate-100 pt-4">
            <p className="label text-[10px] text-slate-400 mb-2">Services</p>
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <NavLink key={s.slug} to={`/services/${s.slug}`} className="flex items-center gap-2 text-slate-600 py-1.5 text-sm">
                  <Icon size={15} className="text-cobalt" /> {s.title}
                </NavLink>
              );
            })}
          </div>
          <div className="border-t border-slate-100 pt-4">
            <p className="label text-[10px] text-slate-400 mb-2">Products — Zoho</p>
            {zohoNav.map((p) => { const PIcon = p.icon; return (
              <NavLink key={p.to} to={p.to} className="flex items-center gap-2 text-slate-600 py-1.5 text-sm"><PIcon size={15} className="text-cobalt" /> {p.label}</NavLink>
            ); })}
            <p className="label text-[10px] text-slate-400 mb-2 mt-3">Products — Absolute</p>
            {absoluteNav.map((p) => { const PIcon = p.icon; return (
              <NavLink key={p.to} to={p.to} className="flex items-center gap-2 text-slate-600 py-1.5 text-sm"><PIcon size={15} className="text-cobalt" /> {p.label}</NavLink>
            ); })}
            <p className="label text-[10px] text-slate-400 mb-2 mt-3">Products — Hostinger <span style={{ color: "#673DE6" }}>(20% Off)</span></p>
            {hostingerNav.map((p) => { const PIcon = p.icon; return (
              <NavLink key={p.label} to={p.to} data-testid={`nav-mobile-${p.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-2 text-slate-600 py-1.5 text-sm"><PIcon size={15} className="text-cobalt" /> {p.label}</NavLink>
            ); })}
          </div>
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <NavLink to="/solutions" className="block text-slate-700 font-medium">Solutions</NavLink>
            <NavLink to="/events" className="block text-slate-700 font-medium" data-testid="nav-mobile-events">Events</NavLink>
            <NavLink to="/resources" className="block text-slate-700 font-medium" data-testid="nav-mobile-resources">Resources</NavLink>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <p className="label text-[10px] text-slate-400 mb-2">Notices</p>
            <NavLink to="/PHP2Shell-security-advisory" data-testid="nav-mobile-notice-php2shell" className="flex items-center gap-2 text-slate-600 py-1.5 text-sm">
              <ShieldAlert size={15} className="text-red-600" /> PHP2Shell Security Advisory
            </NavLink>
          </div>
          <Button to="/contact" className="w-full">Contact Us</Button>
        </div>
      )}
    </header>
  );
}
